// SideMenu.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Avatar,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Box,
  ListItemButton,
  Button, // Import Badge for notification count
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Payment as PaymentIcon,
  BarChart as BarChartIcon,
  Settings as SettingsIcon,
  Description as SubmissionsIcon,
  Public as RegionsIcon,
  AdminPanelSettings as OwnerConsoleIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import { Tooltip, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTour } from '../../tours/TourProvider';
import { useUser } from '../../context/UserContext'; // Import useUser hook
import { canAccessPage, PAGES } from '../../utils/permissionStore'; // Import permission logic
import { settingsAccess } from '../../utils/settingsAccess';
import { getSocket } from '../../utils/socket'; // Import getSocket utility
import { useNotification } from '../../context/NotificationContext'; // Correct hook name and path
import { getDocumentUrl } from '../../utils/authUtils';
import { useOrg } from '../../context/OrgContext';

// Import the dialog components
import EditProfileDialog from './EditProfileDialog';
import UpdateTaxInfoDialog from './UpdateTaxInfoDialog';
import UploadCOIDialog from './UploadCOIDialog';
import UploadBackgroundChecksDialog from './UploadBKChecksDialog';
import LogoutConfirmDialog from '../LogoutConfirmDialog';
import NotificationBell from '../NotificationBell';

const defaultProfileImage = '/images/default-profile.png';

function SideMenu() {
  const navigate = useNavigate();
  const { currentUser, isLoading: userLoading, error: userError } = useUser(); // Get user from context
  // Correctly destructure: useNotification provides 'notification' (singular object) and 'showNotification'
  const { notification, showNotification } = useNotification();
  const { name: companyName, logoUrl: companyLogo, demoMode } = useOrg();
  // First-run tours: replay this page's, open the written help, or switch tours off/on
  const tour = useTour();

  // State variables
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Avatar Dropdown Menu State
  const [anchorEl, setAnchorEl] = useState(null);
  const openProfileMenu = Boolean(anchorEl);

  // Help (?) Menu State
  const [helpAnchorEl, setHelpAnchorEl] = useState(null);
  const closeHelpMenu = () => setHelpAnchorEl(null);
  const handleReplayTour = () => {
    closeHelpMenu();
    if (!tour.replayCurrent()) showNotification('This page has no tour yet. Open Help & tutorials instead.', 'info');
  };
  const handleToursToggle = () => {
    closeHelpMenu();
    if (tour.tours?.skippedAll) {
      tour.resetAll();
      showNotification('Tours are back on. Each page shows its tour the next time you open it.', 'success');
    } else {
      tour.skipAll();
      showNotification('Tours are off. Turn them back on from this menu at any time.', 'info');
    }
  };

  // Notifications Menu State
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
  // const openNotificationsMenu = Boolean(notificationsAnchorEl); // Commented out as menu component is removed

  // Dialog States for Avatar Menu Options
  const [openEditProfileDialog, setOpenEditProfileDialog] = useState(false);
  const [openUpdateTaxInfoDialog, setOpenUpdateTaxInfoDialog] = useState(false);
  const [openUploadCOIDialog, setOpenUploadCOIDialog] = useState(false);
  const [
    openUploadBackgroundChecksDialog,
    setOpenUploadBackgroundChecksDialog,
  ] = useState(false);
  
  // Logout confirmation dialog state
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);

  // --- Socket Listener for Cycle Failure Notifications ---
  useEffect(() => {
    const socket = getSocket(); // Get socket instance using utility

    if (socket && showNotification) { // Check for socket and showNotification
      const handleCycleFailure = (data) => {
        // console.log('Received autoAssignCycleFailed event:', data);
        // Use showNotification from the context
        showNotification( 
          data.message || `Auto-assign cycle failed for PO ${data.poNumber}`, // Construct message directly
          'warning', // Severity
          data.jobId // Pass jobId for potential linking
        );
      };

      socket.on('autoAssignCycleFailed', handleCycleFailure);
      // console.log("Socket listener for 'autoAssignCycleFailed' added.");

      // Cleanup listener on component unmount
      return () => {
        // Check if socket still exists before trying to remove listener
        if (socket) {
            socket.off('autoAssignCycleFailed', handleCycleFailure);
            // console.log("Socket listener for 'autoAssignCycleFailed' removed.");
        }
      };
    } else {
       // console.log("SideMenu: Socket or showNotification not available yet.");
    }
  // Rerun effect if showNotification changes (socket instance likely stable)
  }, [showNotification]); 
  // --- End Socket Listener ---


  // Handlers for Avatar Menu Options
  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseProfileMenu = () => {
    setAnchorEl(null);
  };

  const handleCloseNotificationsMenu = () => {
    setNotificationsAnchorEl(null);
  };

  const handleLogout = () => {
    setOpenLogoutDialog(true);
    handleCloseProfileMenu();
  };
  
  const handleConfirmLogout = () => {
    setOpenLogoutDialog(false);
    const { logout } = require('../../utils/authUtils');
    logout();
  };

  const handleEditProfile = () => {
    setOpenEditProfileDialog(true);
    handleCloseProfileMenu();
  };

  const handleUpdateTaxInfo = () => {
    setOpenUpdateTaxInfoDialog(true);
    handleCloseProfileMenu();
  };

  const handleUploadCOI = () => {
    setOpenUploadCOIDialog(true);
    handleCloseProfileMenu();
  };

  const handleUploadBackgroundChecks = () => {
    setOpenUploadBackgroundChecksDialog(true);
    handleCloseProfileMenu();
  };

  // --- Background Check Upload Handler ---
  const handleBackgroundCheckUpload = async (file) => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No token found for upload.');
      alert('Authentication error. Please log in again.');
      return;
    }
    if (!file) {
      alert('No file selected.');
      return;
    }

    const formData = new FormData();
    formData.append('backgroundCheckFile', file); 

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/profile/upload-background-check`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to upload background check.');
      }

      // console.log('Background check uploaded successfully:', result);
      alert('Background check uploaded successfully!'); 
    } catch (error) {
      console.error('Error uploading background check:', error);
      alert(`Error uploading background check: ${error.message}`);
    } finally {
      setOpenUploadBackgroundChecksDialog(false); 
    }
  };
  // --- End Background Check Upload Handler ---

  // Drawer toggle
  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  // Navigation items using PAGES constants
  const navItems = useMemo(() => {
    // console.log('Building nav items with currentUser:', currentUser);

    // Submissions and Regions are owner / regional-manager pages whatever the stored page permissions say
    const { ownerTools, ownerConsole } = settingsAccess(currentUser);
    const items = [
      { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', pageKey: PAGES.DASHBOARD, tourKey: 'nav-dashboard' },
      ...(ownerTools ? [{ text: 'Submissions', icon: <SubmissionsIcon />, path: '/submissions', pageKey: PAGES.SUBMISSIONS, tourKey: 'nav-submissions' }] : []),
      { text: 'Payments', icon: <PaymentIcon />, path: '/payments', pageKey: PAGES.PAYMENTS, tourKey: 'nav-payments' },
      { text: 'Performance', icon: <BarChartIcon />, path: '/performance', pageKey: PAGES.PERFORMANCE, tourKey: 'nav-performance' },
      { text: 'Settings', icon: <SettingsIcon />, path: '/settings', pageKey: PAGES.SETTINGS, tourKey: 'nav-settings' },
    ];

    if (ownerTools && canAccessPage(PAGES.REGIONS)) {
        // console.log('Adding Regions menu item based on permission');
        items.push({ text: 'Regions', icon: <RegionsIcon />, path: '/regions', pageKey: PAGES.REGIONS, tourKey: 'nav-regions' });
    }
    // Website, marketing and hiring tools: Org 1 owners only, with a password re-check inside
    if (ownerConsole) {
        items.push({ text: 'Owner Console', icon: <OwnerConsoleIcon />, path: '/owner', pageKey: 'Owner', ownerOnly: true, tourKey: 'nav-owner' });
    }
    // The written help is open to everyone who can sign in
    items.push({ text: 'Help & tutorials', icon: <HelpIcon />, path: '/help', pageKey: 'Help', ownerOnly: true, tourKey: 'nav-help' });

    return items;
  }, [currentUser]); // Depend on currentUser from context

  // Memoize profile picture URL to prevent infinite re-renders
  // Must be called before any early returns to follow Rules of Hooks
  const profilePictureUrl = useMemo(() => {
    if (!currentUser?.profilePicture) {
      return defaultProfileImage;
    }
    return getDocumentUrl(currentUser.profilePicture);
  }, [currentUser?.profilePicture]);

  // Handle loading state
  if (userLoading) {
    return (
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Loading User...
          </Typography>
        </Toolbar>
      </AppBar>
    );
  }

  // Handle error state
  if (userError) {
     return (
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1, color: 'error.main' }}>
            Error loading user data.
          </Typography>
           <Button color="inherit" onClick={handleLogout}>Logout</Button>
        </Toolbar>
      </AppBar>
    );
  }

  // Ensure currentUser exists before proceeding
  if (!currentUser) {
     return (
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {companyName}
          </Typography>
           <Button color="inherit" onClick={handleLogout}>Logout</Button>
        </Toolbar>
      </AppBar>
    );
  }

  // Calculate unread notification count - REMOVED as 'notifications' array is not available
  const unreadCount = 0; // Default to 0 as we don't have the list

  return (
    <>
      {/* AppBar */}
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={toggleDrawer}
            size="large"
            data-tour="menu-button"
          >
            <MenuIcon />
          </IconButton>
          {companyLogo && (
            <Box component="img" src={companyLogo} alt="" sx={{ height: 32, width: 'auto', maxWidth: 120, mr: 1.5, objectFit: 'contain', display: { xs: 'none', sm: 'block' } }} />
          )}
          <Typography variant="h6" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }} data-tour="company-name">
            {companyName}
            {demoMode && (
              <Box component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, px: 0.8, py: 0.2, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.22)', textTransform: 'uppercase' }} title="Payments are simulated until Stripe is connected and demo mode is turned off">
                Demo
              </Box>
            )}
          </Typography>
          {/* Help: replay this page's tour, the written help, tours on/off */}
          <Tooltip title="Help">
            <IconButton color="inherit" size="large" aria-label="help" onClick={(e) => setHelpAnchorEl(e.currentTarget)} data-tour="help-button">
              <HelpIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={helpAnchorEl}
            open={Boolean(helpAnchorEl)}
            onClose={closeHelpMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={handleReplayTour} data-tour="help-replay">Replay this page's tour</MenuItem>
            <MenuItem onClick={() => { closeHelpMenu(); navigate('/help'); }} data-tour="help-center">Help &amp; tutorials</MenuItem>
            <Divider />
            <MenuItem onClick={handleToursToggle}>{tour.tours?.skippedAll ? 'Turn tours back on' : "Don't show tours"}</MenuItem>
          </Menu>
          <Box data-tour="bell" sx={{ display: 'inline-flex' }}><NotificationBell /></Box>
          {/* Avatar with Dropdown Menu */}
          <IconButton onClick={handleAvatarClick} size="large" data-tour="avatar">
            <Avatar
              alt={currentUser?.firstName || currentUser?.username || 'User'} // Use user's name or username
              src={profilePictureUrl} // Use memoized profile picture URL
            />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={openProfileMenu}
            onClose={handleCloseProfileMenu}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={handleEditProfile}>Edit Profile</MenuItem>
            <MenuItem onClick={handleUpdateTaxInfo}>Update Tax Info</MenuItem>
            {['OW', 'sOW', 'OWp', 'RM', 'SP'].includes(currentUser?.primaryRole) && (
              <>
                <MenuItem onClick={handleUploadCOI}>Upload COI</MenuItem>
                <MenuItem onClick={handleUploadBackgroundChecks}>
                  Upload Background Checks
                </MenuItem>
              </>
            )}
            <MenuItem onClick={handleLogout}>
              Log Out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer}>
        <Box sx={{ width: 250 }} role="presentation" onClick={toggleDrawer}>
          <List>
            {currentUser ? (
              navItems
                .filter(item => item.ownerOnly || canAccessPage(item.pageKey))
                .map((item) => (
                  <ListItem key={item.text} disablePadding>
                    <ListItemButton onClick={() => navigate(item.path)} data-tour={item.tourKey}>
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </ListItem>
                ))
            ) : (
              <ListItem>
                <ListItemText primary="Loading menu items..." />
              </ListItem>
            )}
          </List>
        </Box>
      </Drawer>

      {/* Dialogs for Avatar Menu Options */}
      <EditProfileDialog
        open={openEditProfileDialog}
        onClose={() => setOpenEditProfileDialog(false)}
        currentUser={currentUser}
      />

      <UpdateTaxInfoDialog
        open={openUpdateTaxInfoDialog}
        onClose={() => setOpenUpdateTaxInfoDialog(false)}
      />

      <UploadCOIDialog
        open={openUploadCOIDialog}
        onClose={() => setOpenUploadCOIDialog(false)}
      />

      <UploadBackgroundChecksDialog
        open={openUploadBackgroundChecksDialog}
        onClose={() => setOpenUploadBackgroundChecksDialog(false)}
        onUpload={handleBackgroundCheckUpload} // Pass the upload handler
      />
      
      {/* Logout Confirmation Dialog */}
      <LogoutConfirmDialog
        open={openLogoutDialog}
        onClose={() => setOpenLogoutDialog(false)}
        onConfirm={handleConfirmLogout}
      />
    </>
  );
}

export default SideMenu;
